import { useCallback, useMemo, useRef, useState } from "react";
import type {
  LayoutChangeEvent,
  ScrollView as RNScrollView,
} from "react-native";
import { useUnistyles } from "react-native-unistyles";

import SNAP_PICKER_SPACING_STEP, {
  SNAP_PICKER_INSET_STEP,
} from "$src/core/constants/snap-picker";
import CustomPlatform from "$src/core/utils/platform/platform";

type UseHorizontalSnapPickerMetricsOptions = {
  /** Force an exact number of items across the width instead of deriving it from `itemMinWidth`. */
  visibleItemCount?: number;
  /** Narrowest an item may get, in spacing steps. Defaults to the platform's own. */
  minItemWidthStep?: number;
  /** How many items there are. Fewer items than fit share the row instead of leaving a gap. */
  totalItems?: number;
};

const useHorizontalSnapPickerMetrics = ({
  visibleItemCount,
  minItemWidthStep,
  totalItems,
}: UseHorizontalSnapPickerMetricsOptions = {}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const { theme } = useUnistyles();
  const scrollViewRef = useRef<RNScrollView | null>(null);

  const itemMinWidth = theme.spacing(
    minItemWidthStep ??
      CustomPlatform.select({
        native: 30,
        tv: 40,
        web: 40,
      }),
  );
  const itemSpacing = theme.spacing(SNAP_PICKER_SPACING_STEP);

  /* Must match HorizontalSnapScrollView's content padding. */
  const contentInset = theme.spacing(SNAP_PICKER_INSET_STEP);

  /** Width items can occupy: the scroll view insets its content, so the full container width would clip the last item. */
  const contentWidth = Math.max(0, containerWidth - contentInset * 2);

  const visibleItems = useMemo(() => {
    // Caller pins the count (e.g. exactly 3 date pills on phone).
    if (visibleItemCount != null) {
      return Math.max(1, visibleItemCount);
    }

    if (!contentWidth) {
      return 1;
    }

    const calculatedVisibleItems = Math.floor(
      (contentWidth + itemSpacing) / (itemMinWidth + itemSpacing),
    );

    // Fewer items than fit stretch to fill the row, leaving no empty slot.
    return Math.max(
      1,
      totalItems != null
        ? Math.min(calculatedVisibleItems, totalItems)
        : calculatedVisibleItems,
    );
  }, [contentWidth, itemMinWidth, itemSpacing, totalItems, visibleItemCount]);

  const itemWidth = useMemo(() => {
    if (!contentWidth) {
      return itemMinWidth;
    }

    return (contentWidth - itemSpacing * (visibleItems - 1)) / visibleItems;
  }, [contentWidth, itemMinWidth, itemSpacing, visibleItems]);

  const snapToInterval = itemWidth + itemSpacing;

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  /** Centres item `index`, clamped so the first and last stay flush instead of leaving a gutter. */
  const centerItem = useCallback(
    (index: number, itemCount: number, animated = true) => {
      if (contentWidth <= 0 || index < 0 || itemCount <= 0) {
        return;
      }

      const contentLength =
        itemCount * itemWidth + (itemCount - 1) * itemSpacing;
      const maxScroll = Math.max(0, contentLength - contentWidth);
      const itemEnd = index * snapToInterval + itemWidth;
      const itemCenter = index * snapToInterval + itemWidth / 2;
      const x =
        itemEnd <= contentWidth
          ? 0
          : Math.min(Math.max(0, itemCenter - contentWidth / 2), maxScroll);

      requestAnimationFrame(() => {
        scrollViewRef.current?.scrollTo({ x, animated });
      });
    },
    [contentWidth, itemSpacing, itemWidth, snapToInterval],
  );

  return {
    scrollViewRef,
    onLayout,
    containerWidth,
    itemWidth,
    snapToInterval,
    centerItem,
  };
};

export default useHorizontalSnapPickerMetrics;
